<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
/* global axios */
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const accountId = computed(() => route.params.accountId);
const caseCounts = ref({ urgent: 0, shipping: 0, returns: 0 });
const taskCounts = ref({ mine: 0, followups: 0 });

const cards = computed(() => [
  {
    title: 'Incidencias urgentes',
    description: 'Casos graves que necesitan atención especial.',
    icon: '🚨',
    count: caseCounts.value.urgent || 0,
    tone: 'bg-red-50 text-red-700',
    route: { name: 'mi_marciana_cases', params: { caseType: 'urgent' } },
  },
  {
    title: 'Mis tareas',
    description: 'Lo que tienes que hacer y completar.',
    icon: '✅',
    count: taskCounts.value.mine || 0,
    tone: 'bg-emerald-50 text-emerald-700',
    route: {
      name: 'internal_inbox',
      query: { filter: 'tasks', task_view: 'mine' },
    },
  },
  {
    title: 'Seguimientos',
    description: 'Casos que hay que volver a comprobar.',
    icon: '⏰',
    count: taskCounts.value.followups || 0,
    tone: 'bg-amber-50 text-amber-800',
    route: {
      name: 'internal_inbox',
      query: { filter: 'tasks', task_view: 'followups' },
    },
  },
  {
    title: 'Problemas de envío',
    description: 'Envíos con un problema todavía sin resolver.',
    icon: '📦',
    count: caseCounts.value.shipping || 0,
    tone: 'bg-orange-50 text-orange-700',
    route: { name: 'mi_marciana_cases', params: { caseType: 'shipping' } },
  },
  {
    title: 'Cambios y devoluciones pendientes',
    description: 'Gestiones iniciadas que todavía no han terminado.',
    icon: '🔄',
    count: caseCounts.value.returns || 0,
    tone: 'bg-violet-50 text-violet-700',
    route: { name: 'mi_marciana_cases', params: { caseType: 'returns' } },
  },
]);

const loadCounts = async () => {
  const [casesResponse, tasksResponse] = await Promise.allSettled([
    axios.get(`/api/v1/accounts/${accountId.value}/dashboard_cases`),
    axios.get(`/api/v1/accounts/${accountId.value}/internal_threads`, {
      params: { page: 1, per_page: 1, filter: 'tasks' },
    }),
  ]);
  if (casesResponse.status === 'fulfilled') {
    caseCounts.value = casesResponse.value.data.counts || caseCounts.value;
  }
  if (tasksResponse.status === 'fulfilled') {
    taskCounts.value = tasksResponse.value.data.meta?.task_counts || taskCounts.value;
  }
};

const openRoute = target => {
  router.push({
    ...target,
    params: { accountId: accountId.value, ...(target.params || {}) },
  });
};

onMounted(loadCounts);
</script>

<template>
  <div class="h-full w-full overflow-y-auto bg-n-background text-n-slate-12">
    <header class="mx-auto flex w-[calc(100%-2rem)] max-w-5xl flex-col gap-5 py-8 md:w-[calc(100%-3rem)] md:flex-row md:items-center md:justify-between md:py-12">
      <div>
        <p class="text-xs font-extrabold uppercase tracking-widest text-violet-700">Centro de control</p>
        <h1 class="mt-1 text-3xl font-bold md:text-4xl">Mi Marciana</h1>
        <p class="mt-2 text-sm text-n-slate-10">Qué es urgente, qué tienes que hacer y qué casos siguen abiertos.</p>
      </div>
      <button
        type="button"
        class="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-n-slate-12 px-5 text-sm font-bold text-white shadow-md"
        @click="router.push({ name: 'conversation_home', params: { accountId }, query: { mi_filter: 'all' } })"
      >
        ✉ Ir a bandeja de entrada
      </button>
    </header>

    <main class="mx-auto grid w-[calc(100%-2rem)] max-w-5xl gap-3 pb-12 md:w-[calc(100%-3rem)] md:grid-cols-2">
      <button
        v-for="card in cards"
        :key="card.title"
        type="button"
        class="flex min-h-28 items-center gap-4 rounded-2xl border border-n-weak bg-n-solid-1 p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
        @click="openRoute(card.route)"
      >
        <span class="grid size-12 shrink-0 place-items-center rounded-2xl text-2xl" :class="card.tone">
          {{ card.icon }}
        </span>
        <span class="min-w-0 flex-1">
          <strong class="block text-base">{{ card.title }}</strong>
          <small class="mt-1 block text-xs leading-5 text-n-slate-10">{{ card.description }}</small>
        </span>
        <span class="grid min-w-8 shrink-0 place-items-center rounded-full bg-n-slate-12 px-2 py-1 text-xs font-extrabold text-white">
          {{ card.count }}
        </span>
        <span class="text-2xl text-n-slate-9">›</span>
      </button>
    </main>
  </div>
</template>
